import { Button } from "../common";
import { useNavigate } from "react-router-dom";
import { formatPrice } from "../../utils/formatPrice";
import { BungalowHouse } from "../../assets/FinancesImg";
import WhiteArrowRightIcon from "../../assets/whiteRightArrow.svg";
import { convertToFormattedAmount } from "../../utils/convertAmount";
import { useShowLoginModalStore } from "../../stores/loginModalStore";
import { useAuth } from "../../context/authProvider";

interface CmaBoxProps {
  address: string;
  zlid: string;
  listingPrice: number;
  valuation: number;
}

const CmaInstant = ({
  address,
  zlid,
  listingPrice,
  valuation,
}: CmaBoxProps) => {
  const navigate = useNavigate();
  const { setShowLoginModal } = useShowLoginModalStore();
  const { authenticated } = useAuth();

  const formattedListingPrice = convertToFormattedAmount(listingPrice);
  const percentageDifference =
    ((valuation - formattedListingPrice) / valuation) * 100;

  const isUnderlisted = percentageDifference > 0;
  const label =
    valuation === 0 || Number.isNaN(valuation)
      ? "N/A"
      : isUnderlisted
      ? "Underlisted"
      : "Overlisted";
  const labelColor = isUnderlisted ? "#6ECE35" : "#FF6B6B";
  const labelBgColor = isUnderlisted ? "#DEF2D3" : "#FFE5E5";

  const percentageColor = isUnderlisted ? "#6ECE35" : "#FF6B6B";
  const arrowDirection = isUnderlisted ? "-rotate-90" : "rotate-90";

  function shortenAddress(address: string) {
    if (!address) return "";

    const parts = address.split(",");
    const street = parts[0]
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
    return street;
  }

  return (
    <div className="bg-[#EEF7E9] relative rounded-2xl lg:py-6 lg:px-7 p-4 grid xl:grid-cols-[190px_minmax(0,1fr)] xl:gap-10">
      <div className="flex xl:flex-col justify-between xl:justify-start">
        <div className="flex flex-col">
          <div className="font-medium sm:text-base text-xs text-z-shark">
            {shortenAddress(address)}
          </div>
          <div className="lg:mb-5 mb-3">
            {label !== "N/A" && (
              <div
                style={{
                  color: labelColor,
                  borderColor: labelColor,
                  backgroundColor: labelBgColor,
                }}
                className="border font-extrabold mt-2 w-fit px-6 py-1.5 rounded lg:text-[#34A853] text-[#6ECE35]"
              >
                {label}
              </div>
            )}
          </div>
        </div>
        <Button
          onClick={() => {
            if (authenticated) {
              navigate(`/listings/${zlid}/cma`);
            } else {
              setShowLoginModal(true);
            }
          }}
          customClassName="justify-between h-[44px] sm:text-base text-sm sm:w-[190px] w-auto sm:p-4 p-1.5"
        >
          Instant CMA
          <img src={WhiteArrowRightIcon} className="sm:ml-0 ml-2" alt="CMA" />
        </Button>
      </div>
      {valuation !== 0 && (
        <div className="bg-[#CDEDBB] rounded-2xl px-5 py-[18px] relative lg:space-y-3 space-y-1 overflow-hidden">
          <div className="text-z-shark sm:text-sm text-xs font-normal">
            Estimated Value
          </div>
          <div className="font-bold text-z-shark sm:text-[28px] text-xl">
            ${formatPrice(valuation)}
          </div>
          {valuation !== 0 && (
            <>
              <div
                className="space-x-1 px-2 py-1 w-fit text-white flex items-center rounded-full"
                style={{
                  backgroundColor: percentageColor,
                }}
              >
                <img
                  width={12}
                  className={arrowDirection}
                  src={WhiteArrowRightIcon}
                  alt="CMA"
                />
                <span className="sm:text-sm text-xs">
                  {percentageDifference.toFixed(1)}%
                </span>
              </div>
              <div className="absolute lg:-top-4 lg:-right-4 top-0 right-0 !mt-0">
                <img
                  className="mix-blend-darken lg:w-[230px] w-[150px]"
                  src={BungalowHouse}
                  alt=""
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export { CmaInstant };
